<template>
  <CustomModal
    noPadding
    class="display-video"
    :is-modal-open="isOpen"
    @toggleModal="closeModal"
    @onClickAway="closeModal"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    :custom-classes="'tw:w-screen tw:h-screen tw:md:h-auto tw:md:max-w-[768px] tw:md:w-[768px] tw:md:rounded-md tw:rounded-none'"
  >
    <template #modalHeader>
      <p class="tw:text-xl tw:text-gray-700">
        {{ $t(title) }}
      </p>
    </template>

    <div class="tw:w-full tw:flex">
      <div class="tw:p-8 tw:flex tw:items-center tw:w-full">
        <div class="tw:relative tw:w-full tw:pb-[65.25%]">
          <div
            data-testid="rate-connect-video-player"
            ref="playerContainerRef"
            class="tw:absolute tw:top-0 tw:left-0 tw:w-full tw:h-full"
          ></div>
        </div>
      </div>
    </div>

    <template #modalFooter>
      <div class="tw:flex tw:w-full tw:justify-end">
        <MainButton @click="closeModal()">{{ $t('close') }}</MainButton>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import Player from '@vimeo/player'
import CustomModal from '@/components/base/CustomModal.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import { useSessionValues } from '@/composables/useSessionValues'

const props = defineProps({
  isOpen: Boolean,
  title: String,
})

const emit = defineEmits(['closeModal'])

const closeModal = () => {
  emit('closeModal')
}

const playerContainerRef = ref(null)
const { language } = useSessionValues()
const location = computed(() => language.value.split('-')?.[0] ?? 'en')

const videoIds = {
  en: '1153511129',
  de: '1161041716',
  it: '1161042438',
}

const videoHashes = {
  en: 'd6fcdc78be',
  de: '12511b5602',
  it: '7b2fd780d9',
}

let player = null

const currentVideoId = computed(() => {
  const availableLocations = Object.keys(videoIds)
  const locale = availableLocations.includes(location.value) ? location.value : 'en'
  return videoIds[locale]
})

const currentHash = computed(() => {
  const availableLocations = Object.keys(videoIds)
  const locale = availableLocations.includes(location.value) ? location.value : 'en'
  return videoHashes[locale]
})

const videoUrl = computed(() => `https://player.vimeo.com/video/${currentVideoId.value}?h=${currentHash.value}`)

const initPlayer = async () => {
  if (!playerContainerRef.value) return

  if (player) {
    player.destroy()
    player = null
  }

  player = new Player(playerContainerRef.value, {
    url: videoUrl.value,
    autoplay: true,
    muted: true,
    controls: true,
    responsive: true,
    autopause: false,
    badge: false,
  })

  try {
    const tracks = await player.getTextTracks()

    if (tracks.length > 0) {
      const userLanguageTrack = tracks.find(track => track.language === location.value)

      if (userLanguageTrack) {
        await player.enableTextTrack(userLanguageTrack.language, userLanguageTrack.kind)
      } else {
        await player.enableTextTrack(tracks[0].language, tracks[0].kind)
      }
    }
  } catch {}
}

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      setTimeout(async () => {
        if (!props.isOpen) return
        await initPlayer()
      }, 100)
    } else {
      if (player) {
        player.destroy()
        player = null
      }
    }
  }
)

watch([currentVideoId, currentHash], () => {
  if (props.isOpen && player) {
    initPlayer()
  }
})

onBeforeUnmount(() => {
  if (player) {
    player.destroy()
    player = null
  }
})
</script>
